import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import Navbar from "../components/Navbar";
import VehicleCard from "../components/VehicleCard";
import { Button } from "../components/ui/button";
import { ArrowRight, ShieldCheck, Sparkles, Clock } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/33345481/pexels-photo-33345481.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/vehicles")
      .then(({ data }) => { if (!cancelled) setVehicles(data.slice(0, 6)); })
      .catch((err) => console.error("[home] vehicles fetch failed:", err?.message || err));
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-400 mb-6">India&apos;s Premium Rental Marketplace</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none mb-8">
              Drive what you<br />
              <span className="italic font-normal text-sky-300">truly desire.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-lg">
              From sport sedans to weekend SUVs — book verified vehicles from trusted vendors across the country. No surprises, no hidden fees.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/vehicles">
                <Button data-testid="hero-browse-btn" className="bg-sky-400 text-[#050B14] hover:bg-sky-300 font-medium px-7 py-6 text-base shadow-[0_0_30px_rgba(56,189,248,0.25)]">
                  Browse vehicles <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="hero-vendor-btn" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/5 px-7 py-6 text-base">
                  Become a vendor
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured vehicles */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Curated fleet</p>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-tight">Featured vehicles</h2>
          </div>
          <Link to="/vehicles" data-testid="see-all-link" className="text-sm text-sky-300 hover:text-sky-200 flex items-center gap-2">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {vehicles.length === 0 ? (
          <div className="bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-12 text-center">
            <p className="text-slate-400 mb-2">No vehicles listed yet.</p>
            <p className="text-sm text-slate-500">Vendors are getting their fleet ready. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
          </div>
        )}
      </section>

      {/* Why RideKaroo (asymmetric) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Why RideKaroo</p>
        <h2 className="text-3xl sm:text-4xl font-normal tracking-tight mb-12 max-w-xl">
          A marketplace built on <span className="italic text-sky-300">trust</span>.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-8">
            <ShieldCheck className="w-8 h-8 text-sky-300 mb-6" />
            <h3 className="text-xl font-medium tracking-tight mb-3">Verified vendors only</h3>
            <p className="text-slate-400 leading-relaxed">Every vendor is reviewed by our team. Documents verified. Vehicles inspected. So you only ever deal with operators we trust.</p>
          </div>
          <div className="md:col-span-5 bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-8">
            <Sparkles className="w-8 h-8 text-sky-300 mb-6" />
            <h3 className="text-xl font-medium tracking-tight mb-3">Transparent pricing</h3>
            <p className="text-slate-400 leading-relaxed">What you see is what you pay. Per-day, per-km — broken down upfront.</p>
          </div>
          <div className="md:col-span-5 bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-8">
            <Clock className="w-8 h-8 text-sky-300 mb-6" />
            <h3 className="text-xl font-medium tracking-tight mb-3">Book in minutes</h3>
            <p className="text-slate-400 leading-relaxed">Pickup, drop, dates — three steps and you&apos;re driving.</p>
          </div>
          <div className="md:col-span-7 bg-gradient-to-br from-sky-400/10 to-transparent border border-sky-400/20 rounded-2xl p-8 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-light tracking-tight mb-3">Own vehicles? List them.</h3>
              <p className="text-slate-300 leading-relaxed">Vendors earn predictably with our commission model. Get paid weekly.</p>
            </div>
            <Link to="/register" className="mt-6">
              <Button data-testid="cta-vendor-register" className="bg-white text-[#050B14] hover:bg-slate-100 w-fit">
                Start earning <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} RideKaroo. All rights reserved.</span>
          <span className="uppercase tracking-[0.2em]">Premium vehicle rental marketplace</span>
        </div>
      </footer>
    </div>
  );
}
